import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import PropTypes from 'prop-types';
import useDebounce from '@/hooks/useDebounce';
import { proj3857, proj4326 } from '@/ol/proj';
import { selectedParcellesIduState } from '@/store';
import { getAdresses, getParcelles } from '@/utils/locationApi';

const DividerTitle = ({ text }) => {
  return (
    <Divider
      component="div"
      role="presentation"
      textAlign="left"
      sx={{
        '&::before, &::after': {
          borderColor: 'primary.main',
        },
      }}
    >
      <Typography variant="overline" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        {text}
      </Typography>
    </Divider>
  );
};

DividerTitle.propTypes = {
  text: PropTypes.string.isRequired,
};

/**
 * SearchLocation component.
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.map - The OpenLayers Map instance.
 * @param {Function} props.onZoom - Function to be called when the map is zoomed.
 */
export default function SearchLocation({ map, onZoom }) {
  const [selectedParcellesIdu, setSelectedParcellesIdu] = useAtom(selectedParcellesIduState);

  /**
   * Converts GeoJSON feature to OpenLayers Geometry.
   * @param {Object} feature - The GeoJSON feature.
   * @returns {ol.geom.Geometry} The OpenLayers Geometry.
   */
  const getOlGeomFromGeojson = (feature) =>
    new GeoJSON({
      featureProjection: proj3857,
      dataProjection: proj4326,
    }).readGeometry(feature.geometry);

  /**
   * Zooms the map to a specific geometry.
   * @param {ol.geom.Geometry} geom - The OpenLayers Geometry.
   * @param {number} zoom - The zoom level.
   */
  const zoomToGeom = (geom, zoom) => {
    const center = getCenter(geom.getExtent());

    // Zoom to location
    map.getView().animate({
      center,
      zoom,
    });

    if (onZoom) {
      onZoom();
    }
  };

  /**
   * Zooms the map to a selected address.
   * @param {Object} adresse - The selected address.
   */
  const zoomToSelectedAdresse = (adresse) => {
    const geom = getOlGeomFromGeojson(adresse);

    const zoom = adresse.properties.type === 'municipality' ? 15 : Math.max(map.getView().getZoom(), 16);

    zoomToGeom(geom, zoom);
  };

  /**
   * Zooms the map to a selected parcel.
   * @param {Object} parcelle - The selected parcel.
   */
  const zoomToSelectedParcelle = (parcelle) => {
    const geom = getOlGeomFromGeojson(parcelle);

    const idu = parcelle.properties.id;

    if (!selectedParcellesIdu.includes(idu)) {
      setSelectedParcellesIdu([...selectedParcellesIdu, idu]);
    }

    zoomToGeom(geom, 16);
  };

  /**
   * Gets the description of an address feature.
   * @param {Object} adresseFeature - The address feature.
   * @returns {string} The description of the address feature.
   */
  const getAdresseFeatureDescription = (adresseFeature) => {
    let description = adresseFeature.properties.label;
    if (adresseFeature.properties.type === 'municipality') {
      description += ` (${adresseFeature.properties.postcode})`;
    }

    return description;
  };

  const [adresseSearchTerm, setAdresseSearchTerm] = useState('');
  const [selectedAdresse, setSelectedAdresse] = useState(null);
  const [adresses, setAdresses] = useState([]);
  const [isAdresseSearching, setIsAdresseSearching] = useState(false);
  const [isParcelleSearching, setIsParcelleSearching] = useState(false);
  const [adresseInput, setAdresseInput] = useState('');
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [parcelles, setParcelles] = useState([]);
  const [prefixe, setPrefixe] = useState('');
  const [section, setSection] = useState('');
  const [numero, setNumero] = useState('');

  // Debounce search term
  const debouncedAdresseSearchTerm = useDebounce(adresseSearchTerm, 500);

  /**
   * Effect for API call to search location.
   */
  useEffect(() => {
    async function searchLocation() {
      setIsAdresseSearching(true);
      const results = await getAdresses(debouncedAdresseSearchTerm);
      setAdresses(results?.data?.features);
      setIsAdresseSearching(false);
    }

    if (debouncedAdresseSearchTerm && debouncedAdresseSearchTerm.length > 2) {
      searchLocation();
    } else {
      setAdresses([]);
      setIsAdresseSearching(false);
    }
  }, [debouncedAdresseSearchTerm]);

  /**
   * Handle change event of the address input.
   * @param {Object} e - The event object.
   */
  const onAdresseInputChange = (e) => {
    const userInputText = e.target.value || '';
    setAdresseSearchTerm(userInputText);
    setAdresseInput(userInputText);
  };

  /**
   * Handle change event of the address selection.
   * @param {Object} e - The event object.
   * @param {Object} adresse - The selected address.
   */
  const onAdresseChange = (e, adresse) => {
    // En mode freeSolo, une saisie libre validée au clavier renvoie une chaîne : il n'y a rien à localiser.
    if (typeof adresse === 'string') {
      return;
    }

    setSelectedAdresse(adresse);

    if (adresse) {
      setAdresseInput(getAdresseFeatureDescription(adresse));
      setAdresses([]);
      zoomToSelectedAdresse(adresse);
    }
  };

  /**
   * Effect for API call to search parcels.
   */
  useEffect(() => {
    async function searchParcelles(params) {
      setIsParcelleSearching(true);
      const { data } = await getParcelles(params);
      setIsParcelleSearching(false);

      if (data) {
        const features = data.features;
        setParcelles(features);

        if (features && features.length === 1) {
          const [firstFeature] = features;
          setSelectedParcelle(firstFeature);
          onSelectedParcelleChange(firstFeature);
        }
      }
    }

    if (section && numero) {
      setSelectedParcelle(null);
      searchParcelles({
        insee: selectedAdresse.properties.citycode,
        prefixe,
        section,
        numero,
      });
    }
  }, [prefixe, section, numero]);

  /**
   * Get the name of a parcel.
   * @param {Object} parcel - The parcel object.
   * @returns {string} The name of the parcel.
   */
  const getParcelleName = ({ properties }) => `${properties.prefixe}-${properties.section}-${properties.numero}`;

  /**
   * Clear the selected parcel.
   */
  const clearParcelle = () => {
    setPrefixe('');
    setSection('');
    setNumero('');
    setSelectedParcelle(null);
    setParcelles([]);
  };

  /**
   * Handle change event of the selected parcel.
   * @param {Object} feature - The selected parcel.
   */
  const onSelectedParcelleChange = (feature) => {
    if (!feature) {
      clearParcelle();
    }
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid size={12}>
          <DividerTitle text="Recherche par adresse" />
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ alignItems: 'center' }}>
        <Grid size={10}>
          <Autocomplete
            freeSolo
            options={adresses}
            getOptionLabel={(option) => getAdresseFeatureDescription(option)}
            inputValue={adresseInput}
            filterOptions={(options) => options}
            onInputChange={onAdresseInputChange}
            onChange={onAdresseChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Adresse, lieu-dit, commune ..."
                variant="standard"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isAdresseSearching && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        )}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={2}>
          <IconButton
            color="primary"
            onClick={() => {
              zoomToSelectedAdresse(selectedAdresse);
            }}
            disabled={!selectedAdresse}
          >
            <SearchIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container sx={{ mt: 1 }} spacing={1}>
        <Grid size={12}>
          <DividerTitle text="Recherche par parcelle" />
        </Grid>
      </Grid>
      <Grid container sx={{ mt: 1 }} spacing={1}>
        <Grid size={4}>
          <TextField
            variant="standard"
            label="Préfixe"
            value={prefixe}
            onChange={(e) => setPrefixe(e.target.value)}
            disabled={!selectedAdresse?.properties?.citycode}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            variant="standard"
            label="Section"
            required
            value={section}
            onChange={(e) => setSection(e.target.value)}
            disabled={!selectedAdresse?.properties?.citycode}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            variant="standard"
            label="Numéro"
            required
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            disabled={!selectedAdresse?.properties?.citycode}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
        <Grid size={10}>
          <Autocomplete
            freeSolo
            disabled={parcelles?.length === 0}
            options={parcelles}
            onChange={(_, feature) => {
              setSelectedParcelle(feature);
              onSelectedParcelleChange(feature);
            }}
            getOptionLabel={getParcelleName}
            value={selectedParcelle}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parcelle"
                variant="standard"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isParcelleSearching && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        )}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.properties.id}>
                {getParcelleName(option)}
              </li>
            )}
          />
        </Grid>
        <Grid size={2}>
          <IconButton
            color="primary"
            onClick={() => {
              zoomToSelectedParcelle(selectedParcelle);
            }}
            disabled={!selectedParcelle}
          >
            <SearchIcon />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
}

SearchLocation.propTypes = {
  map: PropTypes.object.isRequired,
  withPrependIcon: PropTypes.bool,
  onZoom: PropTypes.func,
};
